import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  getRandomSoalEPPS, getRandomSoalPapi,
  hitungSkorEPPS, hitungSkorPapi, EPPS_DIMENSI, PAPI_DIMENSI,
  soalEPPS, soalPapiKostick
} from './bankSoalKepribadian';
import { catatHasilTes } from './progressTracker';
import { useCegahKeluarTes } from './cegahKeluarTes';
import { bacaProgresTes, hapusProgresTes, useAutosaveTes } from './useResumeTes';

const SEMUA_BANK_KEPRIBADIAN = [...soalEPPS, ...soalPapiKostick];
function soalDariMetaKepribadian(meta) {
  const byId = {};
  SEMUA_BANK_KEPRIBADIAN.forEach(s => { byId[s.id] = s; });
  return (meta || []).map(m => (byId[m.id] ? { ...byId[m.id], jenis: m.jenis } : null)).filter(Boolean);
}

const WAKTU_TOTAL = 45 * 60;

function formatWaktu(detik) {
  const m = Math.floor(detik / 60).toString().padStart(2, '0');
  const s = (detik % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function BannerLanjutkanKepribadian({ progresTersimpan, onLanjutkan, onMulaiBaru }) {
  if (!progresTersimpan) return null;
  const labelMode = progresTersimpan.mode === 'epps' ? 'EPPS'
    : progresTersimpan.mode === 'papi' ? 'Papi Kostick'
    : 'Campuran';
  const totalSoal = progresTersimpan.soalMeta?.length || 0;
  return (
    <div className="rounded-xl p-4 mb-5 border-2 border-purple-400 bg-purple-50">
      <p className="text-xs font-bold uppercase tracking-wide text-purple-700 mb-1">⏸️ Ada Pengerjaan Sebelumnya</p>
      <p className="text-sm text-purple-800 mb-3">
        Mode <b>{labelMode}</b>, baru sampai soal <b>{(progresTersimpan.indeks || 0) + 1}/{totalSoal}</b>, sisa waktu <b>{formatWaktu(progresTersimpan.waktuSisa || 0)}</b>.
      </p>
      <div className="flex gap-2">
        <button onClick={onLanjutkan} className="flex-1 py-2 rounded-lg bg-purple-600 text-white text-sm font-bold hover:bg-purple-700 transition">
          Lanjutkan
        </button>
        <button onClick={onMulaiBaru} className="flex-1 py-2 rounded-lg border border-purple-300 text-purple-600 text-sm font-semibold hover:bg-purple-100 transition">
          Mulai Baru
        </button>
      </div>
    </div>
  );
}

// ─── Layar Intro ─────────────────────────────────────────────
function LayarIntro({ onMulai, onBack, tryoutMode, progresTersimpan, onLanjutkan, onMulaiBaru }) {
  const [mode, setMode] = useState(null);

  const MODES = [
    {
      id: 'epps',
      icon: '📋',
      label: 'EPPS',
      desc: '100 soal — pilih pernyataan A atau B',
      detail: 'Edwards Personal Preference Schedule — 15 dimensi kepribadian',
      border: 'border-blue-400', bg: 'bg-blue-50',
      iconBg: 'bg-blue-100', textColor: 'text-blue-700', checkColor: 'text-blue-600',
    },
    {
      id: 'papi',
      icon: '🎯',
      label: 'Papi Kostick',
      desc: '100 soal — nilai 1-4 tiap pernyataan',
      detail: 'Personality and Preference Inventory — 20 faktor kepribadian',
      border: 'border-green-400', bg: 'bg-green-50',
      iconBg: 'bg-green-100', textColor: 'text-green-700', checkColor: 'text-green-600',
    },
    {
      id: 'campuran',
      icon: '🧠',
      label: 'Campuran',
      desc: '50 soal EPPS + 50 soal Papi Kostick',
      detail: 'Hasil EPPS & Papi Kostick ditampilkan terpisah',
      border: 'border-purple-400', bg: 'bg-purple-50',
      iconBg: 'bg-purple-100', textColor: 'text-purple-700', checkColor: 'text-purple-600',
    },
  ];

  const selected = MODES.find(m => m.id === mode);
  const modeCampuran = MODES.find(m => m.id === 'campuran');

  // Dalam konteks Tryout resmi, mode dikunci ke Campuran — semua peserta
  // mengerjakan komposisi soal yang sama. Notice besar biar jelas kenapa
  // tidak ada pilihan mode.
  if (tryoutMode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg max-w-lg w-full p-6">
          <div className="text-center mb-5">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-3xl">🧠</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Tes Kepribadian</h1>
            <p className="text-gray-400 text-sm mt-1">Bagian dari Tryout Resmi</p>
          </div>

          <BannerLanjutkanKepribadian progresTersimpan={progresTersimpan} onLanjutkan={onLanjutkan} onMulaiBaru={onMulaiBaru} />

          <div className="rounded-xl p-4 mb-4 border-2 border-amber-400 bg-amber-50">
            <p className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-1">📌 Perhatian</p>
            <p className="text-sm font-semibold text-amber-800 leading-relaxed">
              Karena ini bagian dari Tryout resmi, mode dikunci otomatis ke <u>Campuran</u> — semua peserta mengerjakan komposisi soal yang sama, tidak bisa memilih mode lain.
            </p>
          </div>

          <div className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 ${modeCampuran.border} ${modeCampuran.bg} mb-5`}>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${modeCampuran.iconBg}`}>
              <span className="text-2xl">{modeCampuran.icon}</span>
            </div>
            <div className="flex-1">
              <p className={`font-bold text-sm ${modeCampuran.textColor}`}>{modeCampuran.label}</p>
              <p className="text-xs text-gray-500">{modeCampuran.desc}</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 mb-5 grid grid-cols-3 gap-3 text-center">
            <div><p className="text-lg font-bold text-gray-800">100</p><p className="text-xs text-gray-500">Soal</p></div>
            <div><p className="text-lg font-bold text-gray-800">45'</p><p className="text-xs text-gray-500">Waktu</p></div>
            <div><p className="text-lg font-bold text-gray-800">~27"</p><p className="text-xs text-gray-500">Per soal</p></div>
          </div>

          <button onClick={() => onMulai('campuran')}
            className="w-full py-3 rounded-xl font-bold border-2 border-purple-400 bg-purple-50 text-purple-700 hover:opacity-90 transition">
            Mulai Sekarang →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-lg w-full p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-3xl">🧠</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Tes Kepribadian</h1>
          <p className="text-gray-400 text-sm mt-1">Pilih mode latihan</p>
        </div>

        <BannerLanjutkanKepribadian progresTersimpan={progresTersimpan} onLanjutkan={onLanjutkan} onMulaiBaru={onMulaiBaru} />

        <div className="space-y-3 mb-5">
          {MODES.map(m => (
            <button key={m.id} onClick={() => setMode(m.id)}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition text-left
                ${mode === m.id ? `${m.border} ${m.bg}` : 'border-gray-200 bg-white hover:border-gray-300'}`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
                ${mode === m.id ? m.iconBg : 'bg-gray-100'}`}>
                <span className="text-2xl">{m.icon}</span>
              </div>
              <div className="flex-1">
                <p className={`font-bold text-sm ${mode === m.id ? m.textColor : 'text-gray-700'}`}>{m.label}</p>
                <p className="text-xs text-gray-500">{m.desc}</p>
                {mode === m.id && <p className={`text-xs mt-1 font-medium ${m.textColor}`}>✦ {m.detail}</p>}
              </div>
              {mode === m.id && <span className={`text-xl font-bold ${m.checkColor}`}>✓</span>}
            </button>
          ))}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
          <p className="text-xs text-amber-700 font-semibold mb-1">⚠️ Petunjuk Penting:</p>
          <ul className="text-xs text-amber-600 space-y-1">
            <li>• Jawab dengan jujur sesuai kepribadian Anda yang sebenarnya</li>
            <li>• Tidak ada jawaban benar atau salah dalam tes ini</li>
            <li>• Timer 45 menit berjalan otomatis</li>
            <li>• Hasil digunakan untuk mengenali profil kepribadian Anda</li>
          </ul>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-5 grid grid-cols-3 gap-3 text-center">
          <div><p className="text-lg font-bold text-gray-800">100</p><p className="text-xs text-gray-500">Soal</p></div>
          <div><p className="text-lg font-bold text-gray-800">45'</p><p className="text-xs text-gray-500">Waktu</p></div>
          <div><p className="text-lg font-bold text-gray-800">~27"</p><p className="text-xs text-gray-500">Per soal</p></div>
        </div>

        <div className="flex gap-3">
          <button onClick={onBack} className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition">← Kembali</button>
          <button onClick={() => mode && onMulai(mode)} disabled={!mode}
            className={`flex-grow py-3 rounded-xl font-bold transition
              ${mode ? `${selected?.border} border-2 ${selected?.bg} ${selected?.textColor} hover:opacity-90` : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
            {mode ? `Mulai ${selected?.label} →` : 'Pilih mode dulu'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Soal EPPS (Pilih A atau B) ──────────────────────────────
function SoalEPPS({ soal, onJawab, jawaban }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <span className="bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">📋 EPPS</span>
        <span className="text-xs text-gray-400">Pilih pernyataan yang paling sesuai dengan diri Anda</span>
      </div>
      <div className="space-y-3">
        {['a', 'b'].map(pilihan => (
          <button key={pilihan} onClick={() => onJawab(pilihan)}
            className={`w-full text-left p-4 rounded-xl border-2 transition
              ${jawaban === pilihan
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'}`}>
            <div className="flex items-start gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm mt-0.5
                ${jawaban === pilihan ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                {pilihan.toUpperCase()}
              </div>
              <p className={`text-sm leading-relaxed ${jawaban === pilihan ? 'text-blue-800 font-medium' : 'text-gray-700'}`}>
                {pilihan === 'a' ? soal.a : soal.b}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Soal Papi Kostick (Skala 1-4) ───────────────────────────
function SoalPapi({ soal, onJawab, jawaban }) {
  const skala = [
    { nilai: '1', label: 'Sangat Tidak Setuju' },
    { nilai: '2', label: 'Tidak Setuju' },
    { nilai: '3', label: 'Setuju' },
    { nilai: '4', label: 'Sangat Setuju' },
  ];

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <span className="bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">🎯 Papi Kostick</span>
        <span className="text-xs text-gray-400">Seberapa setuju pernyataan ini dengan diri Anda?</span>
      </div>
      <div className="bg-green-50 border border-green-100 rounded-xl p-5 mb-5">
        <p className="text-base font-semibold text-gray-800 leading-relaxed">{soal.pernyataan}</p>
        <p className="text-xs text-gray-400 mt-2">Dimensi: {PAPI_DIMENSI[soal.dimensi]}</p>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {skala.map(s => (
          <button key={s.nilai} onClick={() => onJawab(s.nilai)}
            className={`flex flex-col items-center p-3 rounded-xl border-2 transition
              ${jawaban === s.nilai
                ? 'border-green-500 bg-green-100'
                : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50'}`}>
            <span className={`text-2xl font-extrabold mb-1 ${jawaban === s.nilai ? 'text-green-700' : 'text-gray-400'}`}>{s.nilai}</span>
            <span className={`text-[10px] text-center leading-tight ${jawaban === s.nilai ? 'text-green-700 font-semibold' : 'text-gray-400'}`}>{s.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Layar Hasil ─────────────────────────────────────────────
function LayarHasil({ mode, soalList, jawabanUser, onUlang, onBack, tryoutMode, tryoutId }) {
  const [tab, setTab] = useState('ringkasan');

  const soalEPPSList = soalList.filter(s => s.jenis === 'epps');
  const soalPapiList = soalList.filter(s => s.jenis === 'papi');

  const hasilEPPS = soalEPPSList.length > 0 ? hitungSkorEPPS(soalEPPSList, jawabanUser) : null;
  const hasilPapi = soalPapiList.length > 0 ? hitungSkorPapi(soalPapiList, jawabanUser) : null;

  const totalDijawab = jawabanUser.filter(j => j.jawaban !== null && j.jawaban !== undefined && j.jawaban !== '').length;
  const pct = Math.round((totalDijawab / soalList.length) * 100);

  // Catat hasil tes ke progress tracker (kelengkapan jawaban sebagai skor)
  useEffect(() => {
    catatHasilTes('kepribadian', pct, { mode, totalDijawab }, tryoutMode, tryoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl mx-auto space-y-4">

        {/* Header hasil */}
        <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
          <div className="text-4xl mb-2">🧠</div>
          <h2 className="text-lg font-bold text-gray-800 mb-1">Hasil Tes Kepribadian</h2>
          <p className="text-xs text-gray-400 mb-4">
            Mode: {mode === 'epps' ? '📋 EPPS' : mode === 'papi' ? '🎯 Papi Kostick' : '🧠 Campuran'}
          </p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-2xl font-extrabold text-gray-800">{totalDijawab}</p>
              <p className="text-xs text-gray-500">Soal Dijawab</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-2xl font-extrabold text-gray-800">{soalList.length - totalDijawab}</p>
              <p className="text-xs text-gray-500">Tidak Dijawab</p>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
            <div className="bg-purple-500 h-3 rounded-full transition-all duration-1000" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-xs text-gray-400">{pct}% soal terjawab</p>
          <p className="text-xs text-gray-400 mt-2">💡 Hasil ini akan digabungkan ke nilai akhir psikotes kamu</p>
        </div>

        {/* Tab navigasi */}
        <div className="bg-white rounded-2xl shadow p-2 flex gap-2">
          {[
            { id: 'ringkasan', label: '📊 Ringkasan' },
            ...(hasilEPPS ? [{ id: 'epps', label: '📋 Profil EPPS' }] : []),
            ...(hasilPapi ? [{ id: 'papi', label: '🎯 Profil Papi' }] : []),
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 py-2 rounded-xl text-xs font-bold transition
                ${tab === t.id ? 'bg-purple-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Ringkasan */}
        {tab === 'ringkasan' && (
          <div className="space-y-3">
            {hasilEPPS && (
              <div className="bg-white rounded-xl shadow p-4">
                <p className="text-sm font-bold text-blue-700 mb-3">📋 EPPS — Top 5 Dimensi Terkuat</p>
                {Object.entries(hasilEPPS.dimensiCount)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 5)
                  .map(([key, val]) => (
                    <div key={key} className="mb-2">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">{EPPS_DIMENSI[key]}</span>
                        <span className="font-bold text-blue-600">{val}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div className="bg-blue-400 h-2 rounded-full" style={{ width: `${(val / soalEPPSList.length) * 100}%` }} />
                      </div>
                    </div>
                  ))}
              </div>
            )}
            {hasilPapi && (
              <div className="bg-white rounded-xl shadow p-4">
                <p className="text-sm font-bold text-green-700 mb-3">🎯 Papi Kostick — Top 5 Faktor Terkuat</p>
                {Object.entries(hasilPapi.dimensiScore)
                  .filter(([, v]) => v > 0)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 5)
                  .map(([key, val]) => (
                    <div key={key} className="mb-2">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">{PAPI_DIMENSI[key]}</span>
                        <span className="font-bold text-green-600">{val}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div className="bg-green-400 h-2 rounded-full" style={{ width: `${Math.min((val / 20) * 100, 100)}%` }} />
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Tab EPPS Detail */}
        {tab === 'epps' && hasilEPPS && (
          <div className="bg-white rounded-xl shadow p-4">
            <p className="text-sm font-bold text-blue-700 mb-3">📋 Profil EPPS — Semua Dimensi</p>
            {Object.entries(hasilEPPS.dimensiCount)
              .sort((a, b) => b[1] - a[1])
              .map(([key, val]) => (
                <div key={key} className="mb-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-700 font-medium">{EPPS_DIMENSI[key]}</span>
                    <span className="font-bold text-blue-600">{val} pilih</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5">
                    <div className="bg-blue-400 h-2.5 rounded-full transition-all" style={{ width: `${(val / soalEPPSList.length) * 100}%` }} />
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Tab Papi Detail */}
        {tab === 'papi' && hasilPapi && (
          <div className="bg-white rounded-xl shadow p-4">
            <p className="text-sm font-bold text-green-700 mb-3">🎯 Profil Papi Kostick — Semua Faktor</p>
            {Object.entries(hasilPapi.dimensiScore)
              .sort((a, b) => b[1] - a[1])
              .map(([key, val]) => (
                <div key={key} className="mb-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-700 font-medium">{PAPI_DIMENSI[key]}</span>
                    <span className="font-bold text-green-600">{val} poin</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5">
                    <div className="bg-green-400 h-2.5 rounded-full transition-all" style={{ width: `${Math.min((val / 20) * 100, 100)}%` }} />
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Tombol aksi */}
        <div className="bg-white rounded-2xl shadow p-4 flex gap-3">
          <button onClick={onUlang} className="flex-1 py-3 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 transition">🔄 Ulangi</button>
          <button onClick={onBack} className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition">← Menu</button>
        </div>
      </div>
    </div>
  );
}

// ─── Komponen Utama ───────────────────────────────────────────
export default function KepribadianModul({ onBack, tryoutMode = false, tryoutId = null }) {
  const [fase, setFase]           = useState('intro');
  useCegahKeluarTes(fase === 'tes', onBack);
  const [mode, setMode]           = useState(null);
  const [soalList, setSoalList]   = useState([]);
  const [indeks, setIndeks]       = useState(0);
  const [jawabanUser, setJawabanUser] = useState([]);
  const [waktuSisa, setWaktuSisa] = useState(WAKTU_TOTAL);
  const timerRef                  = useRef(null);
  const [progresTersimpan, setProgresTersimpan] = useState(null);

  useEffect(() => {
    setProgresTersimpan(bacaProgresTes('kepribadian', tryoutId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useAutosaveTes(fase === 'tes', 'kepribadian', tryoutId, {
    mode,
    soalMeta: soalList.map(s => ({ id: s.id, jenis: s.jenis })),
    indeks,
    jawabanUser,
    waktuSisa,
  });

  function generateSoal(selectedMode) {
    if (selectedMode === 'epps') return getRandomSoalEPPS(100);
    if (selectedMode === 'papi') return getRandomSoalPapi(100);
    const epps = getRandomSoalEPPS(50);
    const papi = getRandomSoalPapi(50);
    return [...epps, ...papi].sort(() => Math.random() - 0.5);
  }

  const handleMulai = useCallback((selectedMode) => {
    hapusProgresTes('kepribadian', tryoutId);
    const soal = generateSoal(selectedMode);
    setMode(selectedMode);
    setSoalList(soal);
    setIndeks(0);
    setJawabanUser(soal.map(s => ({ id: s.id, jawaban: null })));
    setWaktuSisa(WAKTU_TOTAL);
    setProgresTersimpan(null);
    setFase('tes');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tryoutId]);

  const handleLanjutkan = useCallback(() => {
    if (!progresTersimpan) return;
    const soal = soalDariMetaKepribadian(progresTersimpan.soalMeta);
    if (soal.length === 0) { setProgresTersimpan(null); return; }
    setMode(progresTersimpan.mode);
    setSoalList(soal);
    setIndeks(Math.min(progresTersimpan.indeks || 0, soal.length - 1));
    setJawabanUser(progresTersimpan.jawabanUser || soal.map(s => ({ id: s.id, jawaban: null })));
    setWaktuSisa(progresTersimpan.waktuSisa ?? WAKTU_TOTAL);
    setFase('tes');
  }, [progresTersimpan]);

  const handleMulaiBaruDariBanner = useCallback(() => {
    hapusProgresTes('kepribadian', tryoutId);
    setProgresTersimpan(null);
  }, [tryoutId]);

  useEffect(() => {
    if (fase !== 'tes') return;
    timerRef.current = setInterval(() => {
      setWaktuSisa(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); hapusProgresTes('kepribadian', tryoutId); setFase('hasil'); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase]);

  const handleJawab = useCallback((nilai) => {
    setJawabanUser(prev => prev.map(j => j.id === soalList[indeks].id ? { ...j, jawaban: nilai } : j));
  }, [indeks, soalList]);

  const handleNext = useCallback(() => {
    if (indeks < soalList.length - 1) setIndeks(i => i + 1);
    else { clearInterval(timerRef.current); hapusProgresTes('kepribadian', tryoutId); setFase('hasil'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indeks, soalList.length, tryoutId]);

  const handlePrev = useCallback(() => { if (indeks > 0) setIndeks(i => i - 1); }, [indeks]);
  const handleSelesai = useCallback(() => {
    clearInterval(timerRef.current);
    hapusProgresTes('kepribadian', tryoutId);
    setFase('hasil');
  }, [tryoutId]);
  const handleUlang = useCallback(() => {
    setFase('intro'); setMode(null); setSoalList([]); setIndeks(0);
    setJawabanUser([]); setWaktuSisa(WAKTU_TOTAL);
  }, []);

  if (fase === 'intro') {
    return (
      <LayarIntro
        onMulai={handleMulai}
        onBack={onBack}
        tryoutMode={tryoutMode}
        progresTersimpan={progresTersimpan}
        onLanjutkan={handleLanjutkan}
        onMulaiBaru={handleMulaiBaruDariBanner}
      />
    );
  }
  if (fase === 'hasil') return <LayarHasil mode={mode} tryoutMode={tryoutMode} tryoutId={tryoutId} soalList={soalList} jawabanUser={jawabanUser} onUlang={handleUlang} onBack={onBack} />;

  const soal = soalList[indeks];
  if (!soal) return null;

  const jawabanSoalIni = jawabanUser.find(j => j.id === soal.id)?.jawaban;
  const progress       = ((indeks + 1) / soalList.length) * 100;
  const dijawab        = jawabanUser.filter(j => j.jawaban !== null && j.jawaban !== undefined && j.jawaban !== '').length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className={`font-mono font-bold text-lg px-3 py-1 rounded-lg
              ${waktuSisa < 300 ? 'text-red-600 bg-red-50' : 'text-gray-700 bg-gray-100'}`}>
              ⏱ {formatWaktu(waktuSisa)}
            </div>
            <span className="text-sm text-gray-500 font-semibold">
              <span className="text-purple-600">{indeks + 1}</span> / {soalList.length}
            </span>
            <div className="text-right">
              <span className={`text-xs font-bold px-2 py-1 rounded-full
                ${soal.jenis === 'epps' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                {soal.jenis === 'epps' ? '📋 EPPS' : '🎯 Papi'}
              </span>
              <p className="text-[10px] text-gray-400 mt-0.5">{dijawab} dijawab</p>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-purple-500 h-2 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      {/* Soal */}
      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-white rounded-2xl shadow p-6 mb-4">
          {soal.jenis === 'epps'
            ? <SoalEPPS soal={soal} onJawab={handleJawab} jawaban={jawabanSoalIni} />
            : <SoalPapi soal={soal} onJawab={handleJawab} jawaban={jawabanSoalIni} />
          }
        </div>

        {/* Navigasi */}
        <div className="flex gap-3 mb-4">
          <button onClick={handlePrev} disabled={indeks === 0}
            className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed">
            ← Sebelumnya
          </button>
          {indeks < soalList.length - 1 ? (
            <button onClick={handleNext} className="flex-grow py-3 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 transition">
              Selanjutnya →
            </button>
          ) : (
            <button onClick={handleSelesai} className="flex-grow py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 transition">
              Selesai ✓
            </button>
          )}
        </div>

        {/* Navigator soal mini */}
        <div className="flex flex-wrap gap-1 justify-center">
          {soalList.map((s, i) => {
            const dijawab = jawabanUser.find(j => j.id === s.id)?.jawaban;
            const sudah   = dijawab !== null && dijawab !== undefined && dijawab !== '';
            return (
              <button key={s.id} onClick={() => setIndeks(i)}
                className={`w-7 h-7 text-[10px] rounded-lg font-semibold transition
                  ${i === indeks ? 'bg-purple-600 text-white'
                  : sudah ? 'bg-green-500 text-white'
                  : s.jenis === 'epps' ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                  : 'bg-green-100 text-green-600 hover:bg-green-200'}`}>
                {i + 1}
              </button>
            );
          })}
        </div>
        <p className="text-center text-xs text-gray-400 mt-2">
          🟦 Aktif | 🟩 Dijawab | 🔵 EPPS | 🟢 Papi
        </p>
      </div>
    </div>
  );
}
